import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface HoverPopupWrapperProps {
    popupContent: React.ReactNode;
    delay?: number;
    maxWidth?: string | number;
}

export default function HoverPopupWrapper({
    popupContent,
    delay = 500,
    maxWidth = "none",
}: HoverPopupWrapperProps) {
    const [showPopup, setShowPopup] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [popupSize, setPopupSize] = useState({ width: 0, height: 0 });

    const timerRef = useRef<number | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const parentEl = useRef<HTMLElement | null>(null);

    // find our real parent (the button) once mounted
    useEffect(() => {
        const node = sentinelRef.current?.parentElement;
        if (node && node instanceof HTMLElement) parentEl.current = node;
    }, []);

    useEffect(() => {
        const node = parentEl.current;
        if (!node) return;

        const enter = (e: MouseEvent) => {
            setMousePos({ x: e.clientX, y: e.clientY });
            timerRef.current = window.setTimeout(() => setShowPopup(true), delay);
        };
        const move = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
        const leave = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            setShowPopup(false);
        };

        node.addEventListener("mouseenter", enter);
        node.addEventListener("mousemove", move);
        node.addEventListener("mouseleave", leave);

        return () => {
            node.removeEventListener("mouseenter", enter);
            node.removeEventListener("mousemove", move);
            node.removeEventListener("mouseleave", leave);
        };
    }, [delay]);

    useEffect(() => {
        if (showPopup && popupRef.current) {
            const rect = popupRef.current.getBoundingClientRect();
            setPopupSize({ width: rect.width, height: rect.height });
        }
    }, [showPopup, popupContent]);

    const getPopupStyle = (): React.CSSProperties => {
        const padding = 10;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let left = mousePos.x + padding;
        let top = mousePos.y + padding;

        // flip horizontally if too close to right edge
        if (mousePos.x + popupSize.width + padding > vw) {
            left = mousePos.x - popupSize.width - padding;
        }
        // flip vertically if too close to bottom edge
        if (mousePos.y + popupSize.height + padding > vh) {
            top = mousePos.y - popupSize.height - padding;
        }

        return {
            position: "fixed" as const,
            left,
            top,
            backgroundColor: "#BCB7A8",
            border: "2px solid black",
            padding: "8px",
            boxShadow: "0px 0px 5px rgba(0,0,0,0.3)",
            pointerEvents: "none" as const,
            zIndex: 9999,
            maxWidth,
        };
    };

    return (
        <>
            {/* hidden sentinel lets us find the button DOM node */}
            <span ref={sentinelRef} style={{ display: "none" }} />
            {createPortal(
                showPopup && (
                    <div ref={popupRef} style={getPopupStyle()}>
                        {popupContent}
                    </div>
                ),
                document.body
            )}
        </>
    );
}
